import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { apiList } from '../../api/apiClient';

type EntityType = 'base' | 'dealer' | 'customer' | 'property';

interface FeatureRow {
  feature_key: string;
  display_name: string;
  description: string;
  category: string;
  default_billing_model: 'flat' | 'metered' | 'per_unit';
  default_price_per_unit_cents: number;
  is_premium: boolean;
}

interface OverrideRow {
  entity_type: 'dealer' | 'customer' | 'property';
  entity_id: string;
  feature_key: string;
  rate_cents: number;
  billing_enabled: boolean;
}

interface EntityChoice { id: string; name: string; }

const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
const authHeaders = (): HeadersInit => {
  const t = localStorage.getItem('intrlock_token') || '';
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export const BillingRatesAdmin: React.FC = () => {
  const [features, setFeatures] = useState<FeatureRow[]>([]);
  const [overrides, setOverrides] = useState<OverrideRow[]>([]);
  const [dealers, setDealers] = useState<EntityChoice[]>([]);
  const [customers, setCustomers] = useState<EntityChoice[]>([]);
  const [properties, setProperties] = useState<EntityChoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState<EntityType>('base');
  const [scopeId, setScopeId] = useState<string>('');
  const [editing, setEditing] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [feats, ovs, ds, cs, ps] = await Promise.all([
        fetch(`${API}/stripe/admin/billing-features`, { headers: authHeaders() }).then(r => r.json()).catch(() => []),
        fetch(`${API}/stripe/admin/feature-rate-overrides`, { headers: authHeaders() }).then(r => r.json()).catch(() => []),
        apiList('dealers').catch(() => apiList('integrators').catch(() => [])),
        apiList('customers').catch(() => []),
        apiList('properties').catch(() => []),
      ]);
      setFeatures(Array.isArray(feats) ? feats : []);
      setOverrides(Array.isArray(ovs) ? ovs : []);
      setDealers((Array.isArray(ds) ? ds : []).map((d: any) => ({ id: String(d.id), name: d.name || d.company || d.email || d.id })));
      setCustomers((Array.isArray(cs) ? cs : []).map((c: any) => ({ id: String(c.id), name: c.company || c.company_name || c.name || c.id })));
      setProperties((Array.isArray(ps) ? ps : []).map((p: any) => ({ id: String(p.id), name: p.name || p.id })));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const overrideMap = useMemo(() => {
    const m = new Map<string, OverrideRow>();
    for (const o of overrides) m.set(`${o.entity_type}:${o.entity_id}:${o.feature_key}`, o);
    return m;
  }, [overrides]);

  const entityChoices: EntityChoice[] =
    scope === 'dealer' ? dealers
    : scope === 'customer' ? customers
    : scope === 'property' ? properties
    : [];

  const baseRateDollars = (f: FeatureRow) => Number(f.default_price_per_unit_cents || 0) / 100;
  const overrideOf = (f: FeatureRow): OverrideRow | undefined =>
    scope === 'base' ? undefined : overrideMap.get(`${scope}:${scopeId}:${f.feature_key}`);
  const effectiveRateDollars = (f: FeatureRow): number => {
    const ov = overrideOf(f);
    if (ov) return Number(ov.rate_cents) / 100;
    return baseRateDollars(f);
  };

  const saveBase = async (f: FeatureRow, dollars: number) => {
    setSaving(f.feature_key);
    try {
      const res = await fetch(`${API}/stripe/admin/billing-features/${f.feature_key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ default_rate: dollars, default_price_per_unit_cents: Math.round(dollars * 100) }),
      });
      if (!res.ok) throw new Error(`base PUT ${res.status}`);
      await reload();
    } catch (e: any) {
      alert(`Failed to save base rate: ${e.message}`);
    } finally { setSaving(null); }
  };

  const saveOverride = async (f: FeatureRow, dollarsOrNull: number | null) => {
    if (!scopeId) return alert('Pick a specific entity first.');
    setSaving(f.feature_key);
    try {
      const res = await fetch(`${API}/stripe/admin/feature-rate-overrides`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          entity_type: scope,
          entity_id: scopeId,
          feature_key: f.feature_key,
          rate_cents: dollarsOrNull == null ? null : Math.round(dollarsOrNull * 100),
        }),
      });
      if (!res.ok) throw new Error(`override PUT ${res.status}`);
      await reload();
    } catch (e: any) {
      alert(`Failed to save override: ${e.message}`);
    } finally { setSaving(null); }
  };

  const refreshCounters = async () => {
    setSaving('__refresh');
    try {
      await fetch(`${API}/counters/refresh`, { method: 'POST', headers: authHeaders() });
      alert('Counters refreshed — property monthly_revenue will reflect new rates.');
    } finally { setSaving(null); }
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Loading billing rates…</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billing Rates</h1>
          <p className="text-sm text-gray-500 mt-1">Edit base rates for every feature, or set per-dealer / customer / property overrides.</p>
        </div>
        <Button onClick={refreshCounters} disabled={!!saving}>Refresh All Counters</Button>
      </div>

      <Card>
        <div className="flex flex-wrap items-end gap-3 mb-2">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Scope</label>
            <select
              value={scope}
              onChange={(e) => { setScope(e.target.value as EntityType); setScopeId(''); }}
              className="w-44 border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="base">Base rate (system-wide)</option>
              <option value="dealer">Per-Dealer override</option>
              <option value="customer">Per-Customer override</option>
              <option value="property">Per-Property override</option>
            </select>
          </div>
          {scope !== 'base' && (
            <div className="flex-1 min-w-[280px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">{scope.charAt(0).toUpperCase() + scope.slice(1)}</label>
              <select
                value={scopeId}
                onChange={(e) => setScopeId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">— pick a {scope} —</option>
                {entityChoices.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500">
          Hierarchy: <strong>property override</strong> wins over <strong>customer override</strong> wins over <strong>dealer override</strong> wins over <strong>base rate</strong>. Setting a rate of 0 disables billing for that feature on that entity.
        </p>
      </Card>

      <Card padding={false}>
        <CardHeader title={scope === 'base' ? 'Base Rates' : `Overrides for ${scope}: ${entityChoices.find(c => c.id === scopeId)?.name || '—'}`} />
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-2 font-medium text-gray-600">Feature</th>
              <th className="text-left px-4 py-2 font-medium text-gray-600">Type</th>
              <th className="text-left px-4 py-2 font-medium text-gray-600">Base Rate</th>
              <th className="text-left px-4 py-2 font-medium text-gray-600">{scope === 'base' ? 'Edit' : 'Effective Rate'}</th>
              <th className="text-left px-4 py-2 font-medium text-gray-600">Action</th>
            </tr>
          </thead>
          <tbody>
            {features.map((f) => {
              const ov = overrideOf(f);
              const effective = effectiveRateDollars(f);
              const editKey = `${scope}:${scopeId}:${f.feature_key}`;
              const editVal = editing[editKey] ?? String(effective);
              return (
                <tr key={f.feature_key} className="border-b border-gray-100">
                  <td className="px-4 py-2">
                    <div className="font-medium text-gray-900">{f.display_name}</div>
                    <div className="text-xs text-gray-500">{f.feature_key}{f.is_premium && <Badge color="yellow">Premium</Badge>}</div>
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-600">{f.default_billing_model}</td>
                  <td className="px-4 py-2 text-gray-700">${baseRateDollars(f).toFixed(2)}</td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-1">
                      <span className="text-gray-400">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min={0}
                        value={editVal}
                        onChange={(e) => setEditing({ ...editing, [editKey]: e.target.value })}
                        className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                      {scope !== 'base' && ov && <span className="text-xs text-amber-600">override active</span>}
                      {scope !== 'base' && !ov && <span className="text-xs text-gray-400">inheriting</span>}
                    </div>
                  </td>
                  <td className="px-4 py-2 space-x-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        const v = parseFloat(editVal);
                        if (isNaN(v) || v < 0) return alert('Enter a non-negative number.');
                        if (scope === 'base') saveBase(f, v); else saveOverride(f, v);
                      }}
                      disabled={saving === f.feature_key}
                    >
                      {saving === f.feature_key ? '…' : 'Save'}
                    </Button>
                    {scope !== 'base' && ov && (
                      <Button size="sm" variant="secondary" onClick={() => saveOverride(f, null)} disabled={saving === f.feature_key}>
                        Remove override
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
};
